"use client";

import { LandingCenterPersonnelItemApiModel } from "@/Apis/ClinicApi";
import useLandingAppointmentModal from "@/Areas/Landing/Pages/LandingAppointment/useLandingAppointmentModal";
import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { ChildrenOf } from "@/Helpers/CenterOptions.types";
import { useT } from "@/Hooks";
import { DoctorsSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement } from "react";
import HomeEighteenTeamSectionGenerated, {
  HomeEighteenTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenTeamSection.generated";
import HomeEightTeamSectionGenerated, {
  HomeEightTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeEightTeamSection.generated";
import HomeElevenTeamsSectionGenerated, {
  HomeElevenTeamsSectionGeneratedProps,
} from "../Sections/_generated/HomeElevenTeamsSection.generated";
import HomeFifteenTeamSectionGenerated, {
  HomeFifteenTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenTeamSection.generated";
import HomeFiveTeamSectionGenerated, {
  HomeFiveTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeFiveTeamSection.generated";
import HomeFourDoctorSectionGenerated, {
  HomeFourDoctorSectionGeneratedProps,
} from "../Sections/_generated/HomeFourDoctorSection.generated";
import HomeFourteenTeamSectionTwoGenerated, {
  HomeFourteenTeamSectionTwoGeneratedProps,
} from "../Sections/_generated/HomeFourteenTeamSectionTwo.generated";
import HomeNineTeamSectionGenerated, {
  HomeNineTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeNineTeamSection.generated";
import HomeOneDoctorSectionGenerated, {
  HomeOneDoctorSectionGeneratedProps,
} from "../Sections/_generated/HomeOneDoctorSection.generated";
import HomeSevenTeamSectionGenerated, {
  HomeSevenTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenTeamSection.generated";
import HomeSeventeenTeamSectionGenerated, {
  HomeSeventeenTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenTeamSection.generated";
import HomeSixTeamSectionGenerated, {
  HomeSixTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeSixTeamSection.generated";
import HomeSixteenTeamSectionGenerated, {
  HomeSixteenTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeSixteenTeamSection.generated";
import HomeTenTeamSectionGenerated, {
  HomeTenTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeTenTeamSection.generated";
import HomeThreeTeamSectionGenerated, {
  HomeThreeTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeThreeTeamSection.generated";
import HomeTwelveTeamSectionGenerated, {
  HomeTwelveTeamSectionGeneratedProps,
} from "../Sections/_generated/HomeTwelveTeamSection.generated";
import HomeTwoDoctorSectionGenerated, {
  HomeTwoDoctorSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoDoctorSection.generated";
import {
  buildGeneratedSectionProps,
  GeneratedSectionRenderer,
  renderGeneratedSection,
} from "./generated-section-renderer";
import LegacyDoctorsSection from "./LegacyDoctorsSection";

type DoctorsAppearanceKey = ChildrenOf<"SiteTemplateDoctorsAppearance">;
type GeneratedDoctorsProps = Record<string, unknown>;
type GeneratedDoctorsItem = Record<string, unknown>;
type DoctorDataItem = LandingCenterPersonnelItemApiModel;

const DoctorsAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateDoctorsAppearance;

const defaultDoctorsAppearanceKey: DoctorsAppearanceKey =
  DoctorsAppearance.Default.key;

const titleKeyMatchers = [/H20H2Text$/, /H30H3Text$/, /H40H4Text$/];
const subtitleKeyMatchers = [/DivText$/];
const titleDecorationKeyMatchers = [
  /H20Span\d+SpanText$/,
  /H20H2Text2$/,
  /H40Span\d+SpanText$/,
];
const descriptionKeyMatchers = [/P0PText$/, /PText$/];

const legacyDoctorsRenderer: GeneratedSectionRenderer<DoctorsSectionProps> = (
  props,
) => <LegacyDoctorsSection {...props} />;

function buildGeneratedDoctorsProps<T extends GeneratedDoctorsProps>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return buildGeneratedSectionProps<T>(sectionKey, overrides);
}

function findFirstMatchingKey(
  target: GeneratedDoctorsProps | GeneratedDoctorsItem,
  matchers: RegExp[],
): string | undefined {
  return Object.keys(target).find((key) =>
    matchers.some((matcher) => matcher.test(key)),
  );
}

function setFirstMatchingValue(
  target: GeneratedDoctorsProps | GeneratedDoctorsItem,
  matchers: RegExp[],
  value: unknown,
): void {
  if (value === null || value === undefined || value === "") {
    return;
  }

  const key = findFirstMatchingKey(target, matchers);
  if (!key) {
    return;
  }

  target[key] = value;
}

function setAllMatchingValues(
  target: GeneratedDoctorsProps | GeneratedDoctorsItem,
  matchers: RegExp[],
  value: unknown,
): void {
  for (const key of Object.keys(target)) {
    if (matchers.some((matcher) => matcher.test(key))) {
      target[key] = value;
    }
  }
}

function isGeneratedDoctorsItemsArray(
  items: unknown[],
): items is GeneratedDoctorsItem[] {
  const firstItem = items[0];
  if (!firstItem || typeof firstItem !== "object") {
    return false;
  }

  const keys = Object.keys(firstItem as GeneratedDoctorsItem);
  const score =
    (keys.some((key) => key === "src" || /ImageSrc$/.test(key)) ? 3 : 0) +
    (keys.some((key) => /^(link0Text|h\d+0Text|a0Text|title)$/.test(key))
      ? 3
      : 0) +
    (keys.some((key) => /^(p\d+(P\d+)?Text|description)$/.test(key)) ? 2 : 0) +
    (keys.some((key) => /^(href|link0Href)$/.test(key)) ? 1 : 0);

  return score >= 5;
}

function findDoctorsItemsKeys(props: GeneratedDoctorsProps): string[] {
  return Object.entries(props)
    .flatMap(([key, value]) => {
      if (!Array.isArray(value) || value.length === 0) {
        return [];
      }

      if (!isGeneratedDoctorsItemsArray(value)) {
        return [];
      }

      const itemKeys = Object.keys(value[0] ?? {});
      const score =
        (itemKeys.some(
          (itemKey) => itemKey === "src" || /ImageSrc$/.test(itemKey),
        )
          ? 3
          : 0) +
        (itemKeys.some((itemKey) =>
          /^(link0Text|h\d+0Text|a0Text|title)$/.test(itemKey),
        )
          ? 3
          : 0) +
        (itemKeys.some((itemKey) =>
          /^(p\d+(P\d+)?Text|description)$/.test(itemKey),
        )
          ? 2
          : 0) +
        (itemKeys.some((itemKey) => /^(href|link0Href)$/.test(itemKey))
          ? 1
          : 0) +
        (/(doctor|team|member)/i.test(key) ? 1 : 0);

      return score >= 5 ? [{ key, score }] : [];
    })
    .sort((a, b) => b.score - a.score)
    .map((item) => item.key);
}

function getDoctorDepartments(item: DoctorDataItem): string {
  return item.Departments.map((department) => department.Name)
    .filter(Boolean)
    .join("، ");
}

function getDoctorPrimaryDepartment(item: DoctorDataItem): string {
  return item.Departments.find((department) => department.Name)?.Name ?? "";
}

function isViewAllDoctorsLabel(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return (
    normalized.includes("view all") ||
    normalized.includes("more team") ||
    normalized.includes("all members") ||
    normalized.includes("all experts") ||
    normalized.includes("all doctors")
  );
}

function applyViewAllDoctorsRoute(
  target: GeneratedDoctorsProps,
  viewAllDoctorsText: string,
): void {
  for (const [key, value] of Object.entries(target)) {
    if (typeof value !== "string" || !isViewAllDoctorsLabel(value)) {
      continue;
    }

    const hrefCandidates = [
      key.replace(/LinkText$/, "LinkHref"),
      key.replace(/Span0SpanText$/, "AHref"),
      key.replace(/Text$/, "Href"),
    ];

    for (const hrefKey of hrefCandidates) {
      if (
        hrefKey !== key &&
        Object.prototype.hasOwnProperty.call(target, hrefKey)
      ) {
        target[hrefKey] = "/Doctors";
        target[key] = viewAllDoctorsText;
        break;
      }
    }
  }
}

function getDoctorPrimaryText(item: DoctorDataItem): string {
  return (
    item.BriefDescription || getDoctorDepartments(item) || item.Address || ""
  );
}

function getDoctorSecondaryText(item: DoctorDataItem): string {
  return (
    getDoctorDepartments(item) || item.Address || item.BriefDescription || ""
  );
}

function getDoctorTertiaryText(item: DoctorDataItem): string {
  return (
    item.Address || item.BriefDescription || getDoctorDepartments(item) || ""
  );
}

function applyDoctorContent(
  baseItem: GeneratedDoctorsItem,
  item: DoctorDataItem,
): GeneratedDoctorsItem {
  const nextItem: GeneratedDoctorsItem = {
    ...baseItem,
    key: item.CenterUserId,
  };
  const doctorLink = item.Link || "#";
  const doctorName = item.FullName;
  const primaryText = getDoctorPrimaryText(item);
  const secondaryText = getDoctorSecondaryText(item);
  const tertiaryText = getDoctorTertiaryText(item);

  if ("src" in nextItem && item.ImageUrl) {
    nextItem.src = item.ImageUrl;
  }

  for (const key of Object.keys(nextItem)) {
    if (/(^href$|^link0Href$)/.test(key)) {
      nextItem[key] = doctorLink;
      continue;
    }

    if (/(^alt$|ImageAlt$)/.test(key)) {
      nextItem[key] = doctorName;
      continue;
    }

    if (/^(link0Text|h\d+0Text|a0Text|title)$/.test(key)) {
      nextItem[key] = doctorName;
    }
  }

  if ("p0P0Text" in nextItem) {
    nextItem.p0P0Text = secondaryText;
  }

  if ("description" in nextItem) {
    nextItem.description = primaryText;
  }

  if ("p0Text" in nextItem) {
    nextItem.p0Text = "p0P0Text" in nextItem ? tertiaryText : primaryText;
  }

  if ("p1Text" in nextItem) {
    nextItem.p1Text = tertiaryText;
  }

  if ("span0Text" in nextItem) {
    nextItem.span0Text = getDoctorPrimaryDepartment(item);
  }

  return nextItem;
}

function applyDoctorsSectionOverrides<T extends GeneratedDoctorsProps>(
  generatedProps: T,
  props: DoctorsSectionProps,
  viewAllDoctorsText: string,
): T {
  const nextProps: GeneratedDoctorsProps = {
    ...generatedProps,
  };

  setFirstMatchingValue(nextProps, titleKeyMatchers, props.setting.Title);
  if (props.setting.Title) {
    setAllMatchingValues(nextProps, titleDecorationKeyMatchers, "");
  }

  setFirstMatchingValue(nextProps, subtitleKeyMatchers, props.setting.Subtitle);

  if (props.setting.Description) {
    setFirstMatchingValue(
      nextProps,
      descriptionKeyMatchers,
      <Html value={props.setting.Description} />,
    );
  }

  applyViewAllDoctorsRoute(nextProps, viewAllDoctorsText);

  return nextProps as T;
}

function getDoctorsItems(props: DoctorsSectionProps): DoctorDataItem[] {
  const items = props.dataSource.Items ?? [];

  if (
    props.setting.OnlyBlueTickedDoctorsStatus.Key ===
    CenterOptions.Enum.Status.Active.key
  ) {
    return items.filter((item) => item.BlueTick);
  }

  return items;
}

function applyDoctorsItems<T extends GeneratedDoctorsProps>(
  generatedProps: T,
  props: DoctorsSectionProps,
  viewAllDoctorsText: string,
): T {
  const nextProps = applyDoctorsSectionOverrides(
    generatedProps,
    props,
    viewAllDoctorsText,
  ) as GeneratedDoctorsProps;
  const itemsKeys = findDoctorsItemsKeys(nextProps);
  if (itemsKeys.length === 0) {
    return nextProps as T;
  }

  const doctorsItems = getDoctorsItems(props);
  if (doctorsItems.length === 0) {
    for (const itemsKey of itemsKeys) {
      nextProps[itemsKey] = [];
    }

    return nextProps as T;
  }

  for (const itemsKey of itemsKeys) {
    const defaults = nextProps[itemsKey];
    if (
      !Array.isArray(defaults) ||
      defaults.length === 0 ||
      !isGeneratedDoctorsItemsArray(defaults)
    ) {
      continue;
    }

    nextProps[itemsKey] = doctorsItems.map((item, index) =>
      applyDoctorContent(defaults[index % defaults.length] ?? {}, item),
    );
  }

  return nextProps as T;
}

function applyHomeOneDoctorsItems(
  generatedProps: HomeOneDoctorSectionGeneratedProps,
  props: DoctorsSectionProps,
  viewAllDoctorsText: string,
): HomeOneDoctorSectionGeneratedProps {
  const nextProps = applyDoctorsSectionOverrides(
    generatedProps,
    props,
    viewAllDoctorsText,
  );
  const defaults = nextProps.doctorSectionSlideFadeOutDowns;
  const doctorsItems = getDoctorsItems(props);

  return {
    ...nextProps,
    doctorSectionSlideFadeOutDowns: doctorsItems.map((item, index) => ({
      ...(defaults[index % defaults.length] ?? {}),
      key: item.CenterUserId,
      src: item.ImageUrl,
      alt: item.FullName,
      href: item.Link || "#",
      link0Href2: item.Link || "#",
      link0Href3: item.Link || "#",
      link0Text: getDoctorDepartments(item),
      link0Link0Text: item.FullName,
      p0Text: item.Address || "",
      span0ClassName2: "d-none",
      p0ClassName2: "d-none",
      div0ClassName6: "d-none",
    })),
  };
}

function createDoctorsRenderer<T extends GeneratedDoctorsProps>(
  Component: FC<T>,
  sectionKey: string,
  viewAllDoctorsText: string,
): GeneratedSectionRenderer<DoctorsSectionProps> {
  // eslint-disable-next-line react/display-name
  return (props) => (
    <Component
      {...applyDoctorsItems(
        buildGeneratedDoctorsProps<T>(sectionKey),
        props,
        viewAllDoctorsText,
      )}
    />
  );
}

const GeneratedDoctorsSection: FC<DoctorsSectionProps> = (props) => {
  const t = useT();
  const landingAppointmentModal = useLandingAppointmentModal();
  const bookAppointmentText = t(
    "landing.profile.action.bookAppointment",
    undefined,
    "Book appointment",
  );
  const viewAllDoctorsText = t(
    "landing.doctors.viewAll",
    undefined,
    "View All Doctors",
  );

  const generatedDoctorsRenderers = {
    [defaultDoctorsAppearanceKey]: legacyDoctorsRenderer,
    [DoctorsAppearance.Home1.key]: (sectionProps) => (
      <HomeOneDoctorSectionGenerated
        {...applyHomeOneDoctorsItems(
          buildGeneratedDoctorsProps<HomeOneDoctorSectionGeneratedProps>(
            "HomeOneDoctorSection",
          ),
          sectionProps,
          viewAllDoctorsText,
        )}
        divDiv0Div1Div1Div1Link0LinkProps={(item) => {
          const doctor = getDoctorsItems(sectionProps).find(
            (doctorItem) =>
              String(doctorItem.CenterUserId) === String(item.key),
          );

          if (!doctor) {
            return {};
          }

          return {
            href: "#",
            onClick: async (e) => {
              e.preventDefault();
              e.stopPropagation?.();
              await landingAppointmentModal.showBooking({
                values: {
                  CenterId: doctor.CenterId,
                  CenterUserDoctorId: doctor.CenterUserId,
                },
              });
            },
          };
        }}
      />
    ),
    [DoctorsAppearance.Home2.key]: (sectionProps) => {
      const generatedProps = applyDoctorsItems(
        buildGeneratedDoctorsProps<HomeTwoDoctorSectionGeneratedProps>(
          "HomeTwoDoctorSection",
        ),
        sectionProps,
        viewAllDoctorsText,
      );

      return (
        <HomeTwoDoctorSectionGenerated
          {...generatedProps}
          doctorSection36FadeInUps={generatedProps.doctorSection36FadeInUps.map(
            (item) => ({
              ...item,
              link0Link0Text: bookAppointmentText,
            }),
          )}
          divDiv0Div1Link0LinkProps={(item) => {
            const doctor = getDoctorsItems(sectionProps).find(
              (doctorItem) =>
                String(doctorItem.CenterUserId) === String(item.key),
            );

            if (!doctor) {
              return {};
            }

            return {
              href: "#",
              onClick: async (e) => {
                e.preventDefault();
                e.stopPropagation?.();
                await landingAppointmentModal.showBooking({
                  values: {
                    CenterId: doctor.CenterId,
                    CenterBranchId: doctor.CenterBranch.Id,
                    CenterUserDoctorId: doctor.CenterUserId,
                  },
                });
              },
            };
          }}
        />
      );
    },
    [DoctorsAppearance.Home3.key]:
      createDoctorsRenderer<HomeThreeTeamSectionGeneratedProps>(
        HomeThreeTeamSectionGenerated,
        "HomeThreeTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home4.key]:
      createDoctorsRenderer<HomeFourDoctorSectionGeneratedProps>(
        HomeFourDoctorSectionGenerated,
        "HomeFourDoctorSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home5.key]:
      createDoctorsRenderer<HomeFiveTeamSectionGeneratedProps>(
        HomeFiveTeamSectionGenerated,
        "HomeFiveTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home6.key]:
      createDoctorsRenderer<HomeSixTeamSectionGeneratedProps>(
        HomeSixTeamSectionGenerated,
        "HomeSixTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home7.key]:
      createDoctorsRenderer<HomeSevenTeamSectionGeneratedProps>(
        HomeSevenTeamSectionGenerated,
        "HomeSevenTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home8.key]:
      createDoctorsRenderer<HomeEightTeamSectionGeneratedProps>(
        HomeEightTeamSectionGenerated,
        "HomeEightTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home9.key]:
      createDoctorsRenderer<HomeNineTeamSectionGeneratedProps>(
        HomeNineTeamSectionGenerated,
        "HomeNineTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home10.key]:
      createDoctorsRenderer<HomeTenTeamSectionGeneratedProps>(
        HomeTenTeamSectionGenerated,
        "HomeTenTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home11.key]:
      createDoctorsRenderer<HomeElevenTeamsSectionGeneratedProps>(
        HomeElevenTeamsSectionGenerated,
        "HomeElevenTeamsSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home12.key]:
      createDoctorsRenderer<HomeTwelveTeamSectionGeneratedProps>(
        HomeTwelveTeamSectionGenerated,
        "HomeTwelveTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home14.key]:
      createDoctorsRenderer<HomeFourteenTeamSectionTwoGeneratedProps>(
        HomeFourteenTeamSectionTwoGenerated,
        "HomeFourteenTeamSectionTwo",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home15.key]:
      createDoctorsRenderer<HomeFifteenTeamSectionGeneratedProps>(
        HomeFifteenTeamSectionGenerated,
        "HomeFifteenTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home16.key]:
      createDoctorsRenderer<HomeSixteenTeamSectionGeneratedProps>(
        HomeSixteenTeamSectionGenerated,
        "HomeSixteenTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home17.key]:
      createDoctorsRenderer<HomeSeventeenTeamSectionGeneratedProps>(
        HomeSeventeenTeamSectionGenerated,
        "HomeSeventeenTeamSection",
        viewAllDoctorsText,
      ),
    [DoctorsAppearance.Home18.key]:
      createDoctorsRenderer<HomeEighteenTeamSectionGeneratedProps>(
        HomeEighteenTeamSectionGenerated,
        "HomeEighteenTeamSection",
        viewAllDoctorsText,
      ),
  } satisfies Partial<
    Record<DoctorsAppearanceKey, GeneratedSectionRenderer<DoctorsSectionProps>>
  >;

  return renderGeneratedSection(
    props,
    generatedDoctorsRenderers as Record<
      string,
      (props: DoctorsSectionProps) => ReactElement
    >,
    defaultDoctorsAppearanceKey,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedDoctorsSection;
